import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, Crosshair, Quote, ShieldCheck, Timer, TrendingUp } from "lucide-react";
import { EASE, Reveal, RevealText, SectionHeading } from "@/components/Reveal";
import { BlueprintGrid, Particles, TrackPerspective } from "@/components/Backdrop";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import ProductModal from "@/components/ProductModal";
import { useState } from "react";
import { COMPANY } from "@/data/company";
import { PRODUCTS } from "@/data/products";
import PageLinkCTA from "@/components/PageLinkCTA";

const HERO_LINES = ["ENGINEERING", "THE FUTURE", "OF RAILWAYS"];

function HeroHeadline({ reduced }) {
  return (
    <h1
      data-testid="hero-headline"
      className="font-display text-[clamp(2.7rem,8.5vw,6.8rem)] font-extrabold uppercase leading-[0.96] tracking-tight"
    >
      {HERO_LINES.map((line, li) => (
        <span key={line} className="-mb-[0.05em] block overflow-hidden pb-[0.06em]">
          <motion.span
            className={`block ${li === 0 ? "text-ink" : li === 1 ? "metal-text" : "accent-text"}`}
            initial={reduced ? { opacity: 0 } : { y: "112%" }}
            animate={reduced ? { opacity: 1 } : { y: 0 }}
            transition={{ duration: 1.05, delay: 0.3 + li * 0.16, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

function Hero() {
  const ref = useRef(null);
  const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [3.5, -3.5]), { stiffness: 55, damping: 18 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-4.5, 4.5]), { stiffness: 55, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const onMouseMove = (e) => {
    if (reduced || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative flex min-h-[100svh] items-center overflow-hidden bg-[#F3F7FB]"
      data-testid="hero-section"
    >
      <BlueprintGrid />
      <motion.div style={{ rotateX: rX, rotateY: rY, transformPerspective: 1100 }} className="absolute inset-0">
        <TrackPerspective className="absolute inset-0 h-full w-full" />
      </motion.div>
      <Particles />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/50 via-transparent to-[#F3F7FB]"
      />

      <motion.div style={{ y: contentY, opacity: contentOpacity }} className="wrap relative w-full pt-24">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="mb-7 flex flex-wrap items-center gap-x-4 gap-y-2"
        >
          <span className="chip">{COMPANY.name}</span>
          <span className="hidden h-4 w-px bg-navy-900/20 sm:block" aria-hidden="true" />
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-slate-500 sm:text-xs">
            {COMPANY.tagline}
          </span>
        </motion.div>

        <HeroHeadline reduced={reduced} />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          className="mt-7 max-w-2xl text-lg text-slate-600 sm:text-xl"
        >
          Reliable Railway Electrical, Electromechanical &amp; Engineering Solutions
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1, ease: EASE }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link to="/products" data-testid="hero-explore-products-button" className="btn btn-primary">
            Explore Products
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/contact" data-testid="hero-contact-button" className="btn btn-outline">
            Contact Us
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-7 left-1/2 -translate-x-1/2"
        aria-hidden="true"
      >
        <ChevronDown className="h-6 w-6 animate-scroll-hint text-brand-emeraldDark" />
      </motion.div>
    </section>
  );
}

const ABOUT_POINTS = [
  { label: "Established", value: "2021" },
  { label: "Focus", value: "Railway Engineering" },
  { label: "Range", value: "Electrical & Electromechanical Products" },
  { label: "Mode", value: "Manufacturing & Supply" },
  { label: "Approach", value: "Quality Driven" },
];

const QUALITY_CARDS = [
  { icon: ShieldCheck, title: "Quality", text: "Quality-focused manufacturing practices guide every component we build and supply." },
  { icon: Crosshair, title: "Precision", text: "Precision engineering with careful attention to fit, finish and function." },
  { icon: Timer, title: "Reliability", text: "Engineering for reliable railway applications, where dependability matters most." },
  { icon: TrendingUp, title: "Continuous Improvement", text: "Constantly refining processes, skills and standards across our operations." },
];

const TRACKS_IMG =
  "https://images.unsplash.com/photo-1610611342266-bcc131ecc78c?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Home() {
  const [modalProduct, setModalProduct] = useState(null);
  const featured = PRODUCTS.filter((p) => p.featured).slice(0, 4);

  return (
    <>
      <Hero />
      <Marquee />

      {/* ABOUT PREVIEW — engineering paper */}
      <section className="relative section-pad bg-paper text-ink" data-testid="home-about">
        <BlueprintGrid light />
        <div className="wrap relative grid items-center gap-14 lg:grid-cols-2">
          <div>
            <SectionHeading
              dark={false}
              eyebrow="About Bik Commotrade"
              title="Engineering with Purpose. Manufacturing with Precision."
            />
            <Reveal delay={0.2}>
              <p className="mt-6 leading-relaxed text-slate-700">
                BIK COMMOTRADE (OPC) PVT LTD is an engineering and railway-sector products company involved in
                manufacturing, supplying and developing railway electrical, electromechanical, lighting,
                traction-support and industrial engineering components.
              </p>
              <p className="mt-4 leading-relaxed text-slate-700">
                We serve the Indian railway ecosystem as a professional vendor and engineering solutions company —
                with emphasis on quality, reliability, technical capability and customer requirements.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8 flex flex-wrap gap-3">
                {ABOUT_POINTS.map((p) => (
                  <span
                    key={p.label}
                    className="rounded-full border border-navy-700/15 bg-white px-4 py-2 text-xs font-medium text-navy-900 shadow-sm"
                  >
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-emerald">{p.label}</span>
                    <span className="ml-2">{p.value}</span>
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.4}>
              <Link to="/about" className="btn btn-outline mt-9 !border-navy-700/25 !text-navy-900 hover:!border-brand-emerald hover:!text-brand-emerald">
                More About Us
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="relative">
            <div className="relative overflow-hidden rounded-3xl shadow-[0_40px_90px_rgba(11,19,43,0.35)]">
              <img
                src={TRACKS_IMG}
                alt="Railway tracks with overhead catenary lines"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
              <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-tr from-navy-950/55 via-transparent to-brand-blueGlow/20" />
              <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-brand-emerald via-brand-blueGlow to-brand-emerald" />
            </div>
            <div className="glass absolute -bottom-6 left-6 rounded-2xl px-6 py-4 !border-navy-700/20 !bg-white/85 shadow-xl sm:left-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-emeraldDark">Established</p>
              <p className="font-display text-3xl font-bold text-ink">2021</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FEATURED PRODUCTS — dark */}
      <section className="relative section-pad overflow-hidden bg-[#F3F7FB]" data-testid="home-products">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(50%_40%_at_80%_10%,rgba(0,112,243,0.12),transparent_70%)]"
        />
        <div className="wrap relative">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading
              eyebrow="Product Catalogue"
              title="Railway Components, Engineered to Serve"
              description="A focused range of railway electrical, electromechanical, lighting, traction-support and pipe fitting products."
            />
            <Reveal delay={0.2}>
              <Link to="/products" className="btn btn-outline">
                View All Products
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
            {featured.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} onOpen={setModalProduct} />
            ))}
          </div>
        </div>
      </section>

      {/* QUALITY — engineering paper */}
      <section className="relative section-pad bg-paper text-ink" data-testid="home-quality">
        <BlueprintGrid light />
        <div className="wrap relative">
          <SectionHeading
            dark={false}
            align="center"
            eyebrow="Our Commitment"
            title="Quality Is Built Into Every Component"
            description="Four principles shape the way we design, manufacture and supply for railway applications."
          />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {QUALITY_CARDS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08} className="h-full">
                <div className="group h-full rounded-2xl border border-navy-700/10 bg-white p-7 shadow-sm transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgba(16,185,129,0.18)]">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F3F7FB] text-brand-emeraldDark transition-colors duration-500 group-hover:bg-brand-emerald group-hover:text-navy-950">
                    <c.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-wide">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-700">{c.text}</p>
                  <span aria-hidden="true" className="mt-5 block h-[2px] w-10 bg-gradient-to-r from-brand-emerald to-brand-blueGlow transition-all duration-500 group-hover:w-full" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DIRECTOR TEASER — dark */}
      <section className="relative overflow-hidden bg-[#F3F7FB] py-20 md:py-24" data-testid="home-director">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(45%_55%_at_15%_50%,rgba(16,185,129,0.1),transparent_70%)]"
        />
        <div className="wrap relative max-w-4xl text-center">
          <Reveal>
            <Quote className="mx-auto h-9 w-9 text-brand-emeraldDark" />
          </Reveal>
          <RevealText
            text="Our focus is simple — engineer every product with precision, manufacture it with care, and deliver it reliably."
            as="blockquote"
            delay={0.1}
            className="mt-8 font-display text-2xl font-semibold uppercase leading-snug tracking-wide text-ink sm:text-3xl md:text-4xl"
          />
          <Reveal delay={0.3}>
            <p className="mt-8 font-mono text-xs uppercase tracking-[0.28em] text-slate-500">
              {COMPANY.director.name} — {COMPANY.director.role}
            </p>
            <Link to="/directors-message" className="btn btn-outline mt-9" data-testid="home-director-link">
              Read the Director's Message
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="wrap relative -mb-0 py-20 md:py-24" data-testid="home-cta">
        <PageLinkCTA />
      </section>

      <ProductModal product={modalProduct} onClose={() => setModalProduct(null)} />
    </>
  );
}
